import { vehicleSelected } from 'Core/actions/fitmentSearch';
import { assignLocation, pushToHistory, replaceInHistory, replaceLocation } from 'Core/actions/redirect';
import { setServerRedirect } from 'Core/actions/response';
import { type EpicArg, epicFromHandlers } from 'Core/epics/common';
import history from 'Core/history';
import { serverRedirectSelector } from 'Core/selectors/search';
import type { AppDispatch } from 'Core/store';
import type { SelectionEntry } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import { getUriFromRequest } from 'Modules/converter';
import type { ServerModel } from 'Modules/serverApi/types';
import { addPlatformRoute } from 'Utils/url';

const { hasHtmxResults, isHtmxMode } = requestConfig;

export default epicFromHandlers(
  {
    [assignLocation.type]: ({ state, action: { payload } }) => {
      const serverRedirect = serverRedirectSelector(state);
      window.location.assign(serverRedirect || payload);
    },
    [replaceLocation.type]: ({ action: { payload } }) => window.location.replace(payload),
    [pushToHistory.type]: ({
      action: {
        payload: { location, state },
      },
    }) =>
      // sometimes history manipulations force style recalculation so it's better to do it a bit later
      void setTimeout(() => {
        history.push(location, state);
        window.ga?.('send', 'pageview', window.location.pathname + window.location.search);
      }, 0),
    [replaceInHistory.type]: ({
      action: {
        payload: { location, state },
      },
    }) => {
      if (isHtmxMode && hasHtmxResults) {
        return;
      }

      void setTimeout(() => {
        history.replace(location, state);
      }, 0);
    },
  },
  'redirect',
);

export function tryResponseRedirect(arg: EpicArg, response: ServerModel.SearchResponse): boolean {
  const {
    dispatch,
    action: { meta: { doNotRedirectOnVehicleSelect, onInit } = {}, type: actionType },
  } = arg;

  const isVehicleSelectedAction = actionType === vehicleSelected.type;

  if (isVehicleSelectedAction && (onInit || doNotRedirectOnVehicleSelect)) {
    return false;
  }

  const firstAction = response.Actions?.[0];
  if (firstAction?.Type === 'Redirect' && firstAction.RedirectUrl) {
    const serverRedirect = firstAction.RedirectUrl;
    if (window.location.pathname !== serverRedirect) {
      const redirectUrl = addPlatformRoute(serverRedirect);

      dispatch(setServerRedirect(redirectUrl));
      dispatch(assignLocation(redirectUrl));
      return true;
    }

    return false;
  }

  const { vehicleLandingRedirectToSearch } = response.Extra ?? {};
  const redirectToSearch =
    typeof vehicleLandingRedirectToSearch === 'boolean' ? vehicleLandingRedirectToSearch : false;
  return isVehicleSelectedAction ? tryVehicleRedirect(arg, redirectToSearch) : false;
}

export function tryVehicleRedirect(arg: EpicArg, vehicleLandingRedirectToSearch: boolean): boolean {
  const {
    dispatch,
    action: {
      payload: selectedVehicle,
      meta: {
        isSpecifyDialogOpened,
        isSelectedFromDialog,
        isSelectedFromGarage,
        isSelectedFromHeader,
        isSelectedFromSearch,
        isVehicleSelectionForced,
        isVehicleAutoSelected,
        redirectUrl,
      } = {},
    },
  } = arg;

  const {
    categorySelectionPageUrl,
    isAutoRedirectDisabled,
    isCategorySelectionPageRedirectEnabled,
    isOnCategorySelectionPage,
  } = fitmentSearchConfig;

  const isSelectedFromIsolatedHeader = isSelectedFromHeader && !isOnCategorySelectionPage;
  const isSelectedFromIsolatedDialog =
    isSelectedFromDialog && !isSpecifyDialogOpened && !isVehicleSelectionForced;
  const isSelectedFromSearchWithRedirect = isSelectedFromSearch && redirectUrl;

  const isWidgetRedirectAllowed =
    isSelectedFromIsolatedHeader ||
    isSelectedFromIsolatedDialog ||
    isSelectedFromGarage ||
    isSelectedFromSearchWithRedirect;

  const isVehicleSelectionRedirectAllowed =
    !isVehicleAutoSelected || (isVehicleAutoSelected && !isAutoRedirectDisabled);

  if ((isWidgetRedirectAllowed || vehicleLandingRedirectToSearch) && isVehicleSelectionRedirectAllowed) {
    const selectedVehicleSelection = selectedVehicle.selection;

    if (redirectUrl) {
      return tryVehicleRedirectByUrl(dispatch, redirectUrl, selectedVehicleSelection);
    }

    if (!isCategorySelectionPageRedirectEnabled || !categorySelectionPageUrl) {
      const searchPageRedirectUri = getUriFromRequest(
        { selection: selectedVehicleSelection },
        { goToSearchPage: true },
      );

      if (window.location.pathname !== searchPageRedirectUri.pathname) {
        dispatch(assignLocation(searchPageRedirectUri.href));
        return true;
      }

      return false;
    }

    if (isCategorySelectionPageRedirectEnabled && !isOnCategorySelectionPage) {
      dispatch(
        assignLocation(
          getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: categorySelectionPageUrl })
            .href,
        ),
      );

      return true;
    }
  }

  return false;
}

function tryVehicleRedirectByUrl(
  dispatch: AppDispatch,
  redirectUrl: string,
  selectedVehicleSelection: SelectionEntry[],
) {
  if (redirectUrl && window.location.pathname !== redirectUrl) {
    dispatch(
      assignLocation(
        getUriFromRequest({ selection: selectedVehicleSelection }, { pathname: redirectUrl }).href,
      ),
    );

    return true;
  }

  return false;
}
